<template>
  <Transition name="drawer-slide">
    <div v-if="fieldKey" class="field-detail-overlay" @click.self="close" @touchend.self.prevent="close">
      <div ref="drawerRef" class="field-detail-drawer" @click.stop>
        <div class="drawer-top-bar">
          <div class="field-detail-header">
            <span class="field-detail-title">{{ fieldLabel }}</span>
            <div class="field-meta-row">
              <span class="field-meta-chip"><span class="field-meta-value">50</span> puzzles</span>
              <span class="field-meta-chip">
                <span class="field-meta-value">{{ fieldRatingRange }}</span>
              </span>
              <span class="field-meta-chip"
                ><span class="field-meta-value">{{ fieldSpeed }}</span> km/h</span
              >
            </div>
          </div>
          <div class="drawer-close" @click.stop="close" @touchend.stop.prevent="close">✕</div>
        </div>
        <div class="drawer-scroll-content">
          <div class="field-progress-block">
            <span class="field-top-label" :class="{ 'is-solved': progressPct === 100 }">SOLVED</span>
            <div class="field-progress">
              <div class="field-progress-fill" :style="{ width: progressPct + '%', background: progressColor }" />
            </div>
            <span class="field-progress-pct">{{ progressPct }}%</span>
          </div>

          <span class="field-top-label">TOP 3</span>

          <div class="field-sessions-grid">
            <div
              v-for="(sess, idx) in sessions"
              :key="idx"
              class="field-session-card"
              @click="openSegmentReveal(sess.sessionId, sess.sessionDate)"
            >
              <span class="field-session-accuracy">Acc {{ sess.accuracyPct }}%</span>
              <span class="field-session-success">{{ sess.successPct }}%</span>
              <span class="field-session-time">{{ formatSessionDate(sess.sessionDate) }}</span>
            </div>
            <div v-for="n in placeholderSlots" :key="'ph-' + n" class="field-session-card field-session-placeholder" />
          </div>

          <span class="field-top-label">LONGEST STREAK</span>
          <span class="field-streak-value" :class="{ 'is-milestone': streak >= 50 }">{{ streak || '–' }}</span>

          <span class="field-top-label">TOTAL WALKED</span>
          <div class="field-stat-row-wide">
            <div class="field-stat-wide-half">
              <span class="field-stat-label">{{ localize('walk.reveal.distance') }}</span>
              <span class="field-stat-value">{{ totalDistance }}</span>
            </div>
            <span class="field-stat-wide-divider" />
            <div class="field-stat-wide-half">
              <span class="field-stat-label">{{ localize('walk.reveal.time') }}</span>
              <span class="field-stat-value">{{ totalDuration }}</span>
            </div>
          </div>
        </div>
        <div class="drawer-bottom-bar">
          <div class="field-walk-cta" @click.stop="walkField" @touchend.stop.prevent="walkField">
            Walk {{ fieldLabel }}
          </div>
        </div>
      </div>
    </div>
  </Transition>

  <WalkHistoryReveal
    :is-open="showHistoryReveal"
    :session-date="historyRevealDate"
    @close="showHistoryReveal = false"
    @deleted="showHistoryReveal = false"
  />
</template>

<script lang="ts" setup>
import { localize } from '@/core/gambits';
import { useWalkStore } from '@/stores/walk.store';
import { useDrawerGesture } from '@/views/composables/hooks/useDrawerGesture';
import { useGhostClickGuard } from '@/views/composables/hooks/useGhostClickGuard';
import { computed, ref } from 'vue';
import { bandLetterRange } from '../utils/walk-rating-band';
import WalkHistoryReveal from './WalkHistoryReveal.vue';

const props = defineProps<{ fieldKey: string }>();

const emit = defineEmits<{
  close: [];
  walk: [];
}>();

const walkStore = useWalkStore();
const drawerRef = ref<HTMLElement | null>(null);
const showHistoryReveal = ref(false);
const historyRevealDate = ref('');

const { absorb: absorbGhostClick } = useGhostClickGuard();

useDrawerGesture(drawerRef, () => close());

const fieldStats = computed(() => walkStore.fieldStats.find((f) => f.field === props.fieldKey) ?? null);

const fieldLabel = computed(() => {
  const band = props.fieldKey.slice(-1);
  const speed = props.fieldKey.slice(0, -1);
  return `${band.toUpperCase()}${speed}`;
});

const sessions = computed(() => fieldStats.value?.topSessions ?? []);

const fieldSpeed = computed(() => fieldStats.value?.speedKmh ?? Number(props.fieldKey.slice(0, -1)));

const fieldRatingRange = computed(() => {
  const band = fieldStats.value?.band ?? props.fieldKey.slice(-1);
  return bandLetterRange(band);
});

const bestPct = computed(() => sessions.value[0]?.successPct ?? 0);

const progressPct = computed(() => Math.round(bestPct.value));

const progressColor = computed(() => {
  const ch = Math.round((bestPct.value / 100) * 255);
  return `rgb(${ch}, ${ch}, ${ch})`;
});

const placeholderSlots = computed(() => Math.max(0, 3 - sessions.value.length));

const streak = computed(() => fieldStats.value?.longestStreak ?? 0);

const totalDuration = computed(() => {
  const ms = fieldStats.value?.totalDurationMs ?? 0;
  if (ms === 0) return '-';
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
});

const totalDistance = computed(() => {
  const meters = fieldStats.value?.totalDistanceM ?? 0;
  if (meters === 0) return '-';
  const km = meters / 1000;
  return km < 1 ? `${Math.round(meters)}m` : `${km.toFixed(1)}km`;
});

function close() {
  absorbGhostClick();
  emit('close');
}

function walkField() {
  absorbGhostClick();
  emit('walk');
}

function formatSessionDate(dateStr: string): string {
  const now = new Date();
  const todayKey = now.toLocaleDateString('en-CA');
  const yesterdayKey = new Date(now.getTime() - 86400000).toLocaleDateString('en-CA');

  if (dateStr === todayKey) return 'Today';
  if (dateStr === yesterdayKey) return 'Yesterday';

  const date = new Date(dateStr + 'T00:00:00');
  const weekday = date.toLocaleDateString('en', { weekday: 'short' }).slice(0, 2).toUpperCase();
  const day = date.getDate();
  const month = date.toLocaleDateString('en', { month: 'short' });
  return `${weekday} ${day} ${month}`;
}

async function openSegmentReveal(sessionId: string, date: string) {
  await walkStore.loadHistorySummary(sessionId);
  if (walkStore.historySummary) {
    historyRevealDate.value = date;
    showHistoryReveal.value = true;
  }
}
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

.field-detail-overlay {
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.field-detail-drawer {
  position: relative;
  width: 100%;
  max-height: calc(100vh - env(safe-area-inset-top) - 1rem);
  max-height: calc(100dvh - env(safe-area-inset-top) - 1rem);
  background: var(--color-dark-700);
  border-radius: 1.5rem 1.5rem 0 0;
  border: 1px solid var(--color-white-10);
  border-bottom: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  will-change: transform;
}

.drawer-top-bar {
  position: relative;
  flex-shrink: 0;
  padding-top: 1rem;
  padding-bottom: 1.5rem;
}

.drawer-close {
  position: absolute;
  top: 1rem;
  right: 1.5rem;
  z-index: 1;
  width: 2rem;
  height: 2rem;
  border-radius: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--color-white-50);
  font-size: 16px;
  transition: color 150ms ease;

  @include hover {
    color: var(--color-white);
  }

  @include press;
}

.drawer-scroll-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 0;

  &::-webkit-scrollbar {
    display: none;
  }
}

.field-detail-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-white-70);
  padding: 0 3.25rem;
}

.field-detail-title {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2rem;
  font-size: 18px;
  font-weight: 800;
  letter-spacing: 0.08em;
  color: var(--color-white);
}

.field-meta-row {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
  letter-spacing: 0;
  text-transform: none;
}

.field-meta-chip {
  display: inline-flex;
  align-items: baseline;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid var(--color-white-15);
  background: rgba(255, 255, 255, 0.08);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: none;
  color: var(--color-white-50);
  line-height: 1;
}

.field-meta-value {
  font-size: 14px;
  font-weight: 800;
  color: var(--color-white);
  font-variant-numeric: tabular-nums;
}

.field-progress-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 100%;

  .field-top-label {
    margin-bottom: 0;
  }
}

.field-progress {
  width: calc(100% - 3rem);
  height: 48px;
  border-radius: 12px;
  border: 1px solid var(--color-white-10);
  background: rgba(255, 255, 255, 0.06);
  overflow: hidden;
}

.field-progress-fill {
  height: 100%;
  transition:
    width 300ms ease,
    background 300ms ease;
}

.field-progress-pct {
  font-size: 28px;
  font-weight: 800;
  color: var(--color-white);
  line-height: 1;
  font-variant-numeric: tabular-nums;
}

.field-top-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-white-30);
  text-align: center;
  margin-bottom: -8px;

  &.is-solved {
    color: oklch(0.72 0.17 145);
  }
}

.field-streak-value {
  font-size: 28px;
  font-weight: 800;
  color: var(--color-white);
  text-align: center;
  line-height: 1;

  &.is-milestone {
    color: var(--color-correct);
  }
}

.field-stat-row-wide {
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 300px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.03);
  padding: 10px 0;
}

.field-stat-wide-half {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.field-stat-wide-divider {
  width: 1px;
  height: 28px;
  background: var(--color-white-10);
  flex-shrink: 0;
}

.field-stat-label {
  font-size: 10px;
  font-weight: 600;
  color: var(--color-white-40);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.field-stat-value {
  font-size: 16px;
  font-weight: 700;
  color: var(--color-white);
}

.field-sessions-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  justify-items: center;
  width: 100%;
  padding: 0 16px;
}

.field-session-card {
  width: 100px;
  height: 100px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  padding: 8px 0;
  cursor: pointer;
  transition: transform 80ms ease;

  &:active {
    transform: scale(0.95);
  }

  &.field-session-placeholder {
    cursor: default;

    &:active {
      transform: none;
    }
  }
}

.field-session-accuracy {
  font-size: 11px;
  font-weight: 600;
  color: var(--color-white-40);
  letter-spacing: 0.02em;
}

.field-session-success {
  font-size: 28px;
  font-weight: 800;
  color: var(--color-white);
  line-height: 1;
}

.field-session-time {
  font-size: 11px;
  font-weight: 500;
  color: var(--color-white-30);
}

.drawer-bottom-bar {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 1.5rem 1.5rem var(--safe-bottom-offset);
}

.field-walk-cta {
  width: 100%;
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 28px;
  border-radius: 2rem;
  border: 1px solid var(--color-pink-30);
  background: var(--color-pink-10);
  color: var(--color-white);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;
  white-space: nowrap;
  transition: all 200ms ease;

  &:active {
    transform: scale(0.95);
  }
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: opacity 200ms ease;

  .field-detail-drawer {
    transition: transform 200ms ease;
  }
}

.drawer-slide-enter-from,
.drawer-slide-leave-to {
  opacity: 0;

  .field-detail-drawer {
    transform: translateY(100%);
  }
}
</style>
